import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import {
  Plus, Trash2, ChevronRight, Wand2, Loader2, Bot as BotIcon, X, BookOpen,
} from 'lucide-react';
import PinGateModal from '../components/PinGateModal';
import { toast } from 'sonner';
import { stripMarkers } from '../lib/textMarkers';

// Iter-51.39 — reorder + expand the palette. Previously started with
// warm orange, which stacked directly below the (also orange) Beat
// the Bot hero on single-book libraries — the page read as one big
// orange block. Now leads with sky blue and interleaves teal +
// purple so cards feel distinct even before the child has scrolled.
const CARD_BG = [
  'bg-[#5B8DEF] text-white',     // sky blue
  'bg-[#0D9488] text-white',     // teal
  'bg-[#D97398] text-white',     // rose pink
  'bg-[#F4B740] text-[#2B2140]', // mustard
  'bg-[#4A9B7F] text-white',     // forest green
  'bg-[#8B5CF6] text-white',     // violet
  'bg-[#FF7A45] text-white',     // warm orange (last so 1-book library isn't orange)
];

export default function Books() {
  const nav = useNavigate();
  const { activeChild, uiLang } = useApp();
  const t = useT(uiLang);
  const [books, setBooks] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  // Two flags for the शब्दार्थ audit flow: `scanning` runs the read-only
  // audit; `fixing` runs the batch re-extract after confirmation.
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  // A queued action to run after the PIN gate returns a fresh unlock
  // token. Currently only 'fix-shabdarth' — everything else uses the
  // default `nav('/books/new')` behaviour.
  const [pendingAction, setPendingAction] = useState(null);
  // Iter-51.29 — Beat-the-Bot book chooser sheet (mirrors Home.jsx). Books
  // page now surfaces the same hero card so a child on the library view
  // can jump straight into a bot challenge without hopping back home.
  const [showBotPicker, setShowBotPicker] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/children/${activeChild.child_id}/books`);
    setBooks(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeChild) load(); }, [activeChild]);

  const del = async (id) => {
    if (!window.confirm('Delete this book?')) return;
    await api.delete(`/books/${id}`);
    load();
  };

  // -- शब्दार्थ audit (read-only) → then batch reextract (PIN-gated) --
  const scanShabdarth = async () => {
    setScanning(true);
    setAuditResult(null);
    try {
      const bookIds = books.map(b => b.book_id);
      const { data } = await api.post('/books/audit-shabdarth', { book_ids: bookIds });
      setAuditResult(data);
      if ((data.candidates || []).length === 0) {
        toast.success(`Scanned ${data.total_books_scanned} book(s) — no truncated शब्दार्थ tables found.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally { setScanning(false); }
  };

  const runFix = async () => {
    if (!auditResult || (auditResult.candidates || []).length === 0) return;
    setFixing(true);
    try {
      const { data } = await api.post('/books/reextract-shabdarth', {
        pages: auditResult.candidates.map(c => ({ book_id: c.book_id, page_id: c.page_id })),
      });
      toast.success(`Re-extracted ${data.done} page(s); ${data.failed} failed.`);
      setAuditResult({ ...auditResult, results: data.results });
      // Refresh the books list so any updated titles/pages counts appear.
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && /pin/i.test(String(detail || ''))) {
        setPendingAction('fix-shabdarth');
        setGateOpen(true);
      } else {
        toast.error(`Fix failed: ${detail || `HTTP ${status || 'network'}`}`);
      }
    } finally { setFixing(false); }
  };

  const onPinUnlocked = () => {
    setGateOpen(false);
    const act = pendingAction;
    setPendingAction(null);
    if (act === 'fix-shabdarth') runFix();
    else nav('/books/new');
  };

  // Iter-51.29 — Beat-the-Bot launcher (identical to Home.jsx flow so the
  // two hero cards feel like the same button in two locations).
  function launchQuiz(book) {
    if (!book) return;
    const currentIdx = book.current_page_index || 0;
    const page = book.pages?.[currentIdx];
    if (!page) return;
    const text = stripMarkers(page.text || '').trim();
    nav(`/quiz/${book.book_id}`, {
      state: {
        text,
        language: book.language || 'en',
        cacheKey: `${book.book_id}:page:${page.page_id}`,
        pageIdx: currentIdx,
        chunkIdx: 0,
        pages: book.pages,
        currentChunkText: text,
        bookTitle: book.title || '',
      },
    });
  }
  function onBotHeroTap() {
    if (!books || books.length === 0) return;
    if (books.length === 1) { launchQuiz(books[0]); return; }
    setShowBotPicker(true);
  }

  return (
    <div className="px-6 pt-6 pb-36 max-w-5xl mx-auto overflow-x-hidden">
      {/* Iter-51.29 — Simplified header. One title + one primary action
          ("Add book"). The utility scanner is a subtle icon-only button
          tucked to the left of the primary CTA so it doesn't compete for
          the child's attention while still being discoverable for parents. */}
      <div className="flex justify-between items-center mb-5 gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#2B2140]/60 uppercase tracking-widest">Library</p>
          <h1 className="text-3xl md:text-4xl font-extrabold break-words">{t('books')}</h1>
        </div>
        <div className="flex gap-2 items-center">
          <button
            data-testid="scan-shabdarth-btn"
            onClick={scanShabdarth}
            disabled={scanning || books.length === 0}
            title="Scan every page for truncated शब्दार्थ / word-meaning tables"
            aria-label="Fix शब्दार्थ tables"
            className="w-11 h-11 rounded-full bg-white border-2 border-[#5B8DEF]/40 text-[#5B8DEF] flex items-center justify-center disabled:opacity-40 hover:border-[#5B8DEF] transition-colors"
          >
            {scanning ? <Loader2 size={18} className="animate-spin"/> : <Wand2 size={18} strokeWidth={2.5}/>}
          </button>
          <button
            data-testid="new-book-btn"
            onClick={() => setGateOpen(true)}
            className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2"
          >
            <Plus size={20} strokeWidth={3}/> {t('addBook')}
          </button>
        </div>
      </div>

      {/* Iter-51.29 — Beat the Bot hero card (same visual treatment as
          Home.jsx). Hidden when the library is empty (add-book empty state
          below handles that case). Tap opens a book chooser sheet; single
          book short-circuits into the quiz.
          Iter-51.39 — recoloured to teal to break the orange-on-orange
          stack with book cards below. */}
      {books && books.length > 0 && (
        <button
          data-testid="books-btb-hero"
          onClick={onBotHeroTap}
          className="w-full mb-6 rounded-3xl p-6 text-left relative overflow-hidden transition-transform hover:scale-[1.01] active:scale-[0.99]"
          style={{
            background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
            boxShadow: '0 12px 30px -12px rgba(13,148,136,0.55)',
          }}
        >
          <span aria-hidden="true"
            className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/20 animate-pulse"/>
          <span aria-hidden="true"
            className="absolute right-6 top-6 w-16 h-16 rounded-full bg-white/25 flex items-center justify-center">
            <BotIcon size={32} strokeWidth={2.5} className="text-white"/>
          </span>
          <div className="relative z-10 pr-24">
            <div className="text-xs font-extrabold uppercase tracking-widest text-white/85 mb-1">Beat the Bot</div>
            <h3 className="text-2xl font-extrabold text-white leading-tight">Take on a bot right now</h3>
            <p className="text-sm font-bold text-white/90 mt-1">
              Quick MCQ challenge on any book you've read
            </p>
          </div>
        </button>
      )}

      {gateOpen && (
        <PinGateModal
          onUnlocked={onPinUnlocked}
          onCancel={() => { setGateOpen(false); setPendingAction(null); }}
        />
      )}

      {/* Iter-52.16 — starter library entry point from the Books tab.
          Same destination as the Home "See all" CTA; parents who land
          in the Books tab first can still reach the library without
          a bottom-nav change. Compact so it doesn't compete with the
          child's own books grid. */}
      <button
        data-testid="books-library-link"
        onClick={() => nav('/library')}
        className="w-full mb-6 flex items-center gap-3 rounded-2xl p-4 text-white transition-transform hover:scale-[1.01] active:scale-[0.99] text-left"
        style={{
          background: 'linear-gradient(135deg, #E97140 0%, #FF6B1A 100%)',
          boxShadow: '0 12px 30px -14px rgba(233,113,64,0.55)',
        }}
      >
        <div className="w-11 h-11 rounded-2xl bg-white/25 flex items-center justify-center shrink-0">
          <BookOpen size={22} strokeWidth={2.8}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-white leading-snug text-base">Starter Library</div>
          <div className="text-xs text-white/90 font-bold">Browse all free passages — Levels 1 to 3</div>
        </div>
        <ChevronRight size={20} strokeWidth={3} className="text-white shrink-0"/>
      </button>

      {/* Audit results — shown once scanShabdarth has run. Persists until
          the parent closes it. Layout is unchanged from the previous
          version; only the entry-point button was demoted to an icon. */}
      {auditResult && (
        <div data-testid="shabdarth-audit-panel" className="mb-6 rounded-3xl bg-white border-2 border-[#5B8DEF]/30 p-5">
          <div className="flex justify-between items-start gap-2 mb-3">
            <h3 className="text-lg font-extrabold flex items-center gap-2">
              <Wand2 size={18} className="text-[#5B8DEF]"/>
              शब्दार्थ audit
            </h3>
            <button data-testid="close-audit-btn" onClick={() => setAuditResult(null)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800">Close</button>
          </div>
          {auditResult.results ? (
            <>
              <p className="text-sm font-bold text-slate-700 mb-2">Re-extraction complete:</p>
              <ul data-testid="audit-results-list" className="space-y-1 text-sm">
                {auditResult.results.map((r, i) => {
                  const label = auditResult.candidates?.find(c => c.book_id === r.book_id && c.page_id === r.page_id);
                  return (
                    <li key={i} className={`flex justify-between gap-2 px-3 py-1.5 rounded-lg ${r.ok ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                      <span className="truncate">
                        <span className="font-bold">{label?.book_title || 'Book'}</span> · Page {label?.page_num || '?'}
                      </span>
                      <span className={`text-xs font-bold ${r.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {r.ok ? `✓ ${r.row_count} rows` : `× ${r.reason}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (auditResult.candidates || []).length === 0 ? (
            <p data-testid="audit-clean" className="text-sm text-slate-600">
              Scanned {auditResult.total_books_scanned} book(s). All शब्दार्थ tables look complete.
            </p>
          ) : (
            <>
              <p className="text-sm font-bold text-slate-700 mb-2">
                Found <span className="text-rose-600">{auditResult.candidates.length}</span> page(s) that look like they have a truncated glossary table:
              </p>
              <ul data-testid="audit-candidates-list" className="space-y-1 text-sm mb-3 max-h-48 overflow-y-auto">
                {auditResult.candidates.map((c, i) => (
                  <li key={i} className="flex justify-between gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
                    <span className="truncate"><span className="font-bold">{c.book_title}</span> · Page {c.page_num}</span>
                    <span className="text-xs text-slate-500 flex-shrink-0">{c.row_count} row(s)</span>
                  </li>
                ))}
              </ul>
              <button data-testid="run-shabdarth-fix-btn" onClick={runFix} disabled={fixing}
                className="pushable pushable-primary bg-[#5B8DEF] text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
                {fixing ? <Loader2 size={18} className="animate-spin"/> : <Wand2 size={18} strokeWidth={2.5}/>}
                Re-extract all {auditResult.candidates.length} page(s)
              </button>
              <p className="text-xs text-slate-500 mt-2">This calls the OCR model once per page — may take ~2–5 seconds per page.</p>
            </>
          )}
        </div>
      )}

      {books.length === 0 ? (
        <div className="bg-[#F4B740] rounded-3xl p-10 text-center">
          <BookOpen size={48} strokeWidth={2.5} className="text-[#FF6B1A] mx-auto mb-3"/>
          <p className="text-xl font-bold mb-4">{t('noBooks')}</p>
          <button
            data-testid="empty-add-book-btn"
            onClick={() => setGateOpen(true)}
            className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-7 py-3.5 rounded-full inline-flex items-center gap-2"
          >
            <Plus size={20} strokeWidth={3}/> {t('addBook')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {books.map((b, i) => {
            const bg = CARD_BG[i % CARD_BG.length];
            const onDark = bg.includes('text-white');
            return (
              <div
                data-testid={`book-card-${b.book_id}`}
                key={b.book_id}
                className={`${bg} rounded-3xl p-5 relative flex flex-col transition-transform hover:scale-[1.02]`}
              >
                <button
                  data-testid={`delete-book-${b.book_id}`}
                  onClick={() => del(b.book_id)}
                  aria-label="Delete book"
                  className="absolute top-3 right-3 p-2 rounded-full bg-white/60 hover:bg-white transition-colors"
                >
                  <Trash2 size={16} className="text-slate-500"/>
                </button>
                <div className="flex gap-2 mb-3">
                  {b.pages.slice(0, 3).map(p => (
                    <img
                      key={p.page_id}
                      src={p.image_base64}
                      alt=""
                      className="w-14 h-18 rounded-xl object-cover border-2 border-white shadow-sm"
                    />
                  ))}
                </div>
                <h3 className="text-lg font-extrabold mb-1 line-clamp-2 pr-8">{b.title}</h3>
                <div className={`inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider mb-4 ${onDark ? 'text-white/80' : 'text-slate-600'}`}>
                  <span>{b.pages.length} pages</span>
                  <span>•</span>
                  <span>{b.language.toUpperCase()}</span>
                </div>
                <button
                  data-testid={`open-book-${b.book_id}`}
                  onClick={() => nav(`/book/${b.book_id}`)}
                  className="pushable bg-white text-[#2B2140] font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm self-start mt-auto"
                >
                  Open <ChevronRight size={16} strokeWidth={3}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Iter-51.29 — Beat the Bot book chooser sheet (mirrors Home.jsx). */}
      {showBotPicker && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
          data-testid="books-btb-picker-modal"
          onClick={() => setShowBotPicker(false)}
        >
          <div
            className="bg-white rounded-3xl w-full max-w-md p-4 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div>
                <h3 className="text-lg font-extrabold text-[#2B2140]">Pick a book to quiz on</h3>
                <p className="text-xs font-bold text-slate-500">The bots will draw questions from the passage you read most recently.</p>
              </div>
              <button
                data-testid="books-btb-picker-close"
                onClick={() => setShowBotPicker(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0"
              >
                <X size={16} strokeWidth={3}/>
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
              {books.map(b => {
                const cover = b.pages?.[0]?.image_url || b.pages?.[0]?.image_base64;
                return (
                  <button
                    key={b.book_id}
                    data-testid={`books-btb-pick-${b.book_id}`}
                    onClick={() => { setShowBotPicker(false); launchQuiz(b); }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border-2 border-slate-100 hover:border-[#FF6B1A] transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border-2 border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {cover ? (
                        <img src={cover} alt="" className="w-full h-full object-cover"/>
                      ) : (
                        <BotIcon size={22} className="text-slate-400"/>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-extrabold text-[#2B2140] truncate">{b.title || 'Untitled book'}</div>
                      <div className="text-xs font-bold text-slate-500 truncate">
                        {b.language === 'hi' ? 'Hindi' : 'English'} · {b.pages?.length || 0} pages
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
